import { Toggleable } from '@codex-brightworld/gadgets';
import React, { Fragment, PureComponent } from 'react';
import PropTypes from 'prop-types';
import { CSSTransition } from 'react-transition-group';
import styled, { css } from 'styled-components';
import {
  Checkbox,
  H2, H3,
  OnOffSwitch as DefaultOnOffSwitch,
  Link
} from '@codex-brightworld/basics';
import { Icon } from '@codex-brightworld/icons';

const HEADER_TEXT_SIZES = {
  smaller: '14px',
  normal: '16px',
  bigger: '18px'
};

const HEADER_ICON_SIZES = {
  smaller: '16px',
  normal: '18px',
  bigger: '20px'
};

const Wrapper = styled.div`
  .dc-transition-enter {
    max-height: 0;
    opacity: 0.1;

    &.dc-transition-enter-active {
      max-height: 400px;
      opacity: 1;
      transition: opacity 0.2s ease-in, max-height 0.2s ease-in;
    }
  }

  .dc-transition-exit {
    max-height: 400px;
    opacity: 1;

    &.dc-transition-exit-active {
      max-height: 0;
      opacity: 0.01;
      transition: opacity 0.2s ease-in, max-height 0.2s ease-in;
    }
  }
`;

const ToggleIcon = styled(Icon)`
  width: 22px;
  height: 22px;

  svg {
    width: 22px;
    height: 22px;
  }
`;

const HeaderHeading = styled(H3)`
  margin: 0 0 ${props => props.theme.spacing[2]} 0;
  font-size: ${props => HEADER_TEXT_SIZES[props.size]};

  ${Icon} {
    position: relative;
    top: -1px;
    margin-right: ${props => props.theme.spacing[2]};
    width: ${props => HEADER_ICON_SIZES[props.size]};
    height: ${props => HEADER_ICON_SIZES[props.size]};
  }
`;

export const GroupErrorMessage = styled.p`
    margin: 10px 0;
    color: ${props => props.themes.colors.price.default};
`;

const ErrorMessage = styled.p`
  margin: ${props => props.theme.spacing[2]} 0 0;
  color: #cb0000;
`;

const OnOffSwitch = styled(DefaultOnOffSwitch)`
  & ~ & {
    margin-top: ${props => props.theme.spacing[2]};
  }
`;

const OnOffSwitchDescription = styled.div`
  color: ${props => props.theme.colors.shades[1]};
`;

const OnOffSwitchLink = styled(Link)`
  position: relative;
  top: -2px;
  margin-left: ${props => props.theme.spacing[1]};
  padding: ${props => props.theme.spacing[1]} 0;
`;

const OnOffSwitchIcon = styled(Icon)`
  margin-top: 3px;
`;

const ContactChannelsBoxDescription = styled.p`
  margin: 0 0 7px 0;
`;

const ContactChannelsBox = styled.div`
  margin-top: ${props => props.theme.spacing[2]};
  padding: ${props => props.theme.spacing[2]};
  padding-left: 68px;
  background: ${props => props.theme.colors.message.hover};
  color: ${props => props.theme.colors.shades[1]};

  & + span ${OnOffSwitch} {
    margin-top: ${props => props.theme.spacing[2]};
  }

  ${props => props.noSelection && css`
    background: #fefaf1;
  `};

  ${props => props.hasValidationError && css`
    background: #ffefef;

    ${ContactChannelsBoxDescription} {
      color: ${props.theme.colors.price.default};
    }
  `};
`;

const ContactChannelsBoxItems = styled.div`
  display: flex;
  flex-wrap: wrap;
  margin-bottom: -(${props => props.theme.spacing[2]});

  .dc-checkbox-wrapper {
    min-width: auto;
    margin-right: ${props => props.theme.spacing[3]};
    margin-bottom: ${props => props.theme.spacing[2]};
    padding: 0;

    &:hover {
      background-color: transparent;
    }

    &:before {
      display: none;
    }
  }
`;

const InnerWrapperContent = styled.div``;

const InnerWrapper = styled.div`
  background: ${props => props.background};

  ${props => props.border === 'none' && css`
    ${InnerWrapperContent} {
      margin-bottom: ${props.theme.spacing[2]};
      padding: ${props.theme.spacing[2]};

      @media ${props.theme.breakpoints.desktop} {
        padding: ${props.theme.spacing[4]};
      }
    }
  `};

  ${props => props.border === 'inner' && css`
    ${InnerWrapperContent} {
      margin-bottom: ${props.theme.spacing[1]};
      padding: ${props.theme.spacing[2]};
      border: 1px solid ${props.theme.colors.shades[5]};

      @media ${props.theme.breakpoints.desktop} {
        padding: ${props.theme.spacing[4]};
      }
    }
  `};

  ${props => props.border === 'outer' && css`
    margin-bottom: ${props.theme.spacing[1]};
    padding: ${props.theme.spacing[2]};
    border: 1px solid ${props.theme.colors.shades[5]};

    @media ${props.theme.breakpoints.desktop} {
      padding: ${props.theme.spacing[4]};
    }
  `};

  ${props => props.border === 'outer-heading-bg' && css`
    margin-bottom: ${props.theme.spacing[1]};
    padding: 0;
    padding-bottom: ${props.theme.spacing[2]};
    border: 1px solid ${props.theme.colors.shades[5]};

    ${HeaderHeading} {
      margin-bottom: 0;
      padding: ${props.theme.spacing[2]};
      background: #efefef;
    }

    ${InnerWrapperContent} {
      padding: ${props.theme.spacing[2]};

      @media ${props.theme.breakpoints.desktop} {
        padding: ${props.theme.spacing[4]};
      }
    }
  `};
`;

/* eslint-disable max-len */

/**
 * Behavior of this component:
 *
 * - When any of checkboxes from the list is selected, whole group is active.
 * - When last checkbox is deselected whole group gets inactive.
 * - When group has only one checkbox and group is toggled active - the checkbox get active automatically.
 * - When group has no items it behaves like single toggle
 * - Checkbox name is **NOT** magically composed. What is send to `item.name` became name of the checkbox input.
 */

/* eslint-enable max-len */

class StayInTouchBlock extends PureComponent {

  static processGroupsFromProps(props) {
    const groupsFromProps = {};

    props.contactChannelGroups.forEach((group) => {
      groupsFromProps[group.name] = {
        isToggleActive: group.isActive ||
          (group.items && group.items.some(item => item.isChecked)),
        errorMessage: group.errorMessage,
        items: group.items
          ? group.items.map(item => ({
            name: item.name,
            isChecked: item.isChecked === true
          }))
          : []
      };
    });

    return groupsFromProps;
  }

  static addIconToDescription(group, icon) {
    return (
      <div>
        {group.toggleDescription.split(' ').slice(0, -1).join(' ')}
        {' '}
        <span>
          {group.toggleDescription.split(' ').slice(-1)}
          {icon}
        </span>
      </div>
    );
  }

  constructor(props) {
    super(props);

    this.state = {
      activePopup: null,
      groups: StayInTouchBlock.processGroupsFromProps(props)
    };

    this.closePopup = this.closePopup.bind(this);
    this.openPopup = this.openPopup.bind(this);
    this.switchToggle = this.switchToggle.bind(this);
    this.renderGroup = this.renderGroup.bind(this);
  }

  openPopup(e, groupName) {
    e.preventDefault();
    e.stopPropagation();

    this.setState({
      activePopup: groupName
    });
  }

  closePopup() {
    this.setState({
      activePopup: null
    });
  }

  updateChannelGroupsInState(contactChannelGroups) {
    this.setState({
      groups: StayInTouchBlock.processGroupsFromProps({
        contactChannelGroups
      })
    });
  }

  /**
   * @param {string} groupName
   * @param {boolean=} forceValue If set, new value will be set instead of negating current value
   */
  async switchToggle(groupName, forceValue) {
    // I know it is not nice to disable eslint rules but I need the state in the variable due
    // to two setState calls.
    // TODO: refactor the behaviour
    const isToggleActive = forceValue !== undefined
      ? forceValue
      // eslint-disable-next-line react/no-access-state-in-setstate
      : !this.state.groups[groupName].isToggleActive;

    const group = isToggleActive
      ? StayInTouchBlock.processGroupsFromProps(this.props)[groupName]
      : this.state.groups[groupName];
    let items = group.items.map(item => ({ ...item }));

    let changedCheckboxes = [];

    this.props.onInteraction(groupName, isToggleActive);

    if (isToggleActive && items.length === 1) { // Check the checkbox when group has only one
      items[0].isChecked = true;

      changedCheckboxes.push({
        name: items[0].name,
        isChecked: items[0].isChecked
      });
    } else if (isToggleActive) { // Check all checkboxes on toggle according to props
      changedCheckboxes = items;
    } else if (!isToggleActive) { // Uncheck all checkboxes on toggle deactivation
      items = items.map(item => ({
        ...item,
        isChecked: false
      }));

      changedCheckboxes = items;
    }

    // eslint-disable-next-line react/no-access-state-in-setstate
    const prevState = this.state;

    this.setState({
      groups: {
        ...prevState.groups,
        [groupName]: {
          ...prevState.groups[groupName],
          errorMessage: undefined,
          isToggleActive: isToggleActive,
          items: items
        }
      }
    });

    if (changedCheckboxes.length) {
      try {
        await this.props.onCheckboxChanged(changedCheckboxes, items, true);
      } catch (errorMessage) {
        this.setState({
          ...prevState,
          groups: {
            ...prevState.groups,
            [groupName]: {
              ...prevState.groups[groupName],
              errorMessage
            }
          }
        });
      }
    }
  }

  /**
   * @param {string} groupName
   * @param {number} itemIndex
   * @param {boolean=} forceValue If set, new value will be set instead of negating current value
   */
  async toggleCheckbox(groupName, itemIndex, forceValue) {
    const items = this.state.groups[groupName].items.map(item => ({ ...item }));

    items[itemIndex].isChecked = forceValue !== undefined
      ? forceValue
      : !items[itemIndex].isChecked;

    let { isToggleActive } = this.state.groups[groupName];

    // Deactivate group toggle when all checkboxes are unchecked
    if (this.props.closeWhenAllChannelsOff && items.every(item => !item.isChecked)) {
      isToggleActive = false;
    }

    this.props.onInteraction();

    this.setState(prevState => ({
      groups: {
        ...prevState.groups,
        [groupName]: {
          ...prevState.groups[groupName],
          errorMessage: undefined,
          isToggleActive: isToggleActive,
          items: items
        }
      }
    }));

    try {
      await this.props.onCheckboxChanged([
        {
          name: items[itemIndex].name,
          isChecked: items[itemIndex].isChecked
        }
      ], items, false);
    } catch (errorMessage) {
      this.setState(prevState => ({
        ...prevState,
        groups: {
          ...prevState.groups,
          [groupName]: {
            ...prevState.groups[groupName],
            errorMessage
          }
        }
      }));
    }
  }

  isGroupActive(groupName) {
    return this.state.groups[groupName].isToggleActive;
  }

  isCheckboxChecked(groupName, checkboxIndex) {
    return this.state.groups[groupName].items[checkboxIndex].isChecked;
  }

  isValid(groupName) {
    return !this.isGroupActive(groupName) ||
      this.state.groups[groupName].items.some(item => item.isChecked);
  }

  static renderHeader(props) {
    return (
      <HeaderHeading size={props.headerSize}>
        {props.headerIcon && <Icon icon={props.headerIcon} />}
        {props.headerText}
      </HeaderHeading>
    );
  }

  static renderExpandableText(props) {
    return (
      <Toggleable>
        {({ open, toggle }) => (
          <div
            onClick={e => e.stopPropagation()}
          >
            <Link
              href="#toggle"
              onClick={(e) => {
                e.preventDefault();
                toggle(!open);
              }}
              data-interaction={!open ? props.expandableTextInteraction : undefined}
            >
              <span>{props.privacyBoxLabel}</span>
              <ToggleIcon icon="arrowright" rotate={open ? 270 : 90} />
            </Link>
            <div>
              {open && props.privacyBoxDescription}
            </div>
          </div>
        )}
      </Toggleable>
    );
  }

  renderGroup(group) {
    const isGroupActive = this.isGroupActive(group.name);

    return (
      <Fragment key={group.name}>
        {group.hasValidationError && group.groupErrorMessage && (
          <GroupErrorMessage>
            {group.groupErrorMessage}
          </GroupErrorMessage>
        )}
        {this.renderGroupSwitch(group)}
        {this.state.groups[group.name].errorMessage && (
          <ErrorMessage>
            {this.state.groups[group.name].errorMessage}
          </ErrorMessage>
        )}
        <CSSTransition
          in={isGroupActive}
          classNames="dc-transition"
          timeout={200}
        >
          {isGroupActive ? this.renderContactChannelsBox(group) : <div />}
        </CSSTransition>
      </Fragment>
    );
  }

  renderGroupSwitch(group) {
    const { props } = this;

    const isGroupActive = this.isGroupActive(group.name);

    const icon = group.providesPopup && (
      <OnOffSwitchLink
        href="#more-info"
        onClick={e => this.openPopup(e, group.name)}
        data-interaction={group.popupIconInteraction}
      >
        <OnOffSwitchIcon icon="info" />
      </OnOffSwitchLink>
    );

    return (
      <OnOffSwitch
        id="stayInTouchSwitch"
        active={isGroupActive}
        onClick={() => this.switchToggle(group.name)}
        name={group.name}
        label=""
        diFieldId={props.diFieldId}
      >
        <OnOffSwitchDescription>
          {icon
            ? StayInTouchBlock.addIconToDescription(group, icon)
            : group.toggleDescription}
        </OnOffSwitchDescription>
      </OnOffSwitch>
    );
  }

  renderContactChannelsBox(group) {
    const { state } = this;

    const groupFromState = state.groups[group.name];

    if (!group.items) {
      return <noscript />;
    }

    return (
      <ContactChannelsBox
        noSelection={groupFromState.items.every(item => !item.isChecked)}
        hasValidationError={group.hasValidationError}
      >
        <ContactChannelsBoxDescription>{group.description}</ContactChannelsBoxDescription>
        <ContactChannelsBoxItems>
          {group.items.map((item, itemIndex) => (
            <Checkbox
              attrs={{
                container: {
                  className: 'dc-checkbox-wrapper'
                }
              }}
              key={item.name}
              name={item.name}
              checked={this.isCheckboxChecked(group.name, itemIndex)}
              onCheck={() => this.toggleCheckbox(group.name, itemIndex)}
              value="1"
              id={`stayInTouch${item.name}`}
            >
              {item.label}
            </Checkbox>
          ))}
        </ContactChannelsBoxItems>
      </ContactChannelsBox>
    );
  }

  render() {
    const { props, state } = this;

    const renderExpandableTextOutside = [
      'outer',
      'outer-heading-bg'
    ].indexOf(props.borderStyle) > -1;

    return (
      <Wrapper className={props.className} data-qa={props.qa}>
        <InnerWrapper border={props.borderStyle} background={props.innerBackground}>
          {props.displayHeader && StayInTouchBlock.renderHeader(props)}
          <InnerWrapperContent>
            {props.contactChannelGroups.map(this.renderGroup)}
          </InnerWrapperContent>
          {!renderExpandableTextOutside && StayInTouchBlock.renderExpandableText(props)}
          {state.activePopup &&
            props.popupRenderer(state.activePopup, this.closePopup, props, state)}
        </InnerWrapper>
        {renderExpandableTextOutside && StayInTouchBlock.renderExpandableText(props)}
      </Wrapper>
    );
  }
}

StayInTouchBlock.displayName = 'StayInTouchBlock';

StayInTouchBlock.propTypes = {
  borderStyle: PropTypes.oneOf([
    'none',
    'inner',
    'outer',
    'outer-heading-bg',
    'custom'
  ]),
  innerBackground: PropTypes.string,
  className: PropTypes.string,
  closeWhenAllChannelsOff: PropTypes.bool,
  contactChannelGroups: PropTypes.arrayOf(PropTypes.shape({
    /**
     * Unique name for group
     */
    name: PropTypes.string,
    /**
     * Description message for group
     */
    description: PropTypes.string,
    /**
     * If `true` display icon next to toggle description.
     */
    providesPopup: PropTypes.bool,
    /**
     * Description of group toggle
     */
    toggleDescription: PropTypes.string,
    /**
     * Function which receives boolean as parameter and return string for toggle label.
     * Props `onLabel` resp. `offLabel` are used when not provided.
     */
    toggleLabelFactory: PropTypes.func,
    /**
     * When any item has `isChecked === true` whole group will be rendered as active by default.
     */
    items: PropTypes.arrayOf(PropTypes.shape({
      name: PropTypes.string,
      label: PropTypes.string,
      isChecked: PropTypes.bool
    })),
    /**
     * If `true` description and channels box will display error state
     */
    hasValidationError: PropTypes.bool,
    /**
     * If set, error message will be displayed beneath the toggle.
     * Can be used for network errors etc.
     */
    errorMessage: PropTypes.string,
    popupIconInteraction: PropTypes.string
  })),
  displayHeader: PropTypes.bool,
  headerComponent: PropTypes.shape({}),
  headerIcon: PropTypes.string,
  /**
   * Affects size of text and size of icon
   */
  headerSize: PropTypes.oneOf([
    'smaller',
    'normal',
    'bigger'
  ]),
  headerText: PropTypes.node,
  offLabel: PropTypes.string,
  /**
   * Callback called on checkbox value change.
   * Receives array of changed item objects `{name: string, isChecked: boolean}`.
   *
   * If rejected promise is returned. State is reverted to it's previous state.
   * Reject with some message to display error:
   *
   * Promise.reject('Error happened')
   *
   * @param {array} changedItems
   * @param {array} allItems
   * @param {boolean} changeFromToggle
   */
  onCheckboxChanged: PropTypes.func,
  onInteraction: PropTypes.func,
  onLabel: PropTypes.string,
  /**
   * Renders popup according to active popup group.
   *
   * **Do not forget to set enable popup for specific group by setting `providesPopup` value!**
   *
   * @param {string} activeGroup group name of active group
   * @param {function} closePopup function to call when popup should be closed
   * @param {object} parentProps StayInTouchBlock component props
   * @param {object} parentState StayInTouchBlock component state
   */
  popupRenderer: PropTypes.func,
  privacyBoxDescription: PropTypes.node,
  privacyBoxLabel: PropTypes.node,
  expandableTextInteraction: PropTypes.string,
  diFieldId: PropTypes.string,
  qa: PropTypes.string
};

StayInTouchBlock.defaultProps = {
  borderStyle: 'none',
  innerBackground: 'transparent',
  className: undefined,
  closeWhenAllChannelsOff: false,
  contactChannelGroups: [],
  displayHeader: true,
  headerComponent: H2,
  headerIcon: undefined,
  headerSize: 'normal',
  headerText: '[headerText]',
  offLabel: 'Off',
  onCheckboxChanged: () => {},
  onInteraction: () => {},
  onLabel: 'On',
  popupRenderer: () => {},
  privacyBoxDescription: '[privacyBoxDescription]',
  privacyBoxLabel: '[privacyBoxLabel]',
  expandableTextInteraction: undefined,
  diFieldId: undefined,
  qa: undefined
};

export default StayInTouchBlock;
